import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useSearchParams,
  Navigate,
} from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Toaster } from "sonner";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import HomePage from "./pages/HomePage";
import GlobalShippingPage from "./pages/GlobalShippingPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import ManualOrderPaymentPage from "./pages/ManualOrderPaymentPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import SupabaseAuthCallback from "./pages/SupabaseAuthCallback";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminSubmitLinkPage from "./pages/admin/AdminSubmitLinkPage";
import AdminPendingPaymentsPage from "./pages/AdminPendingPaymentsPage";
import SubmitProductPage from "./pages/SubmitProductPage";
import MyCatalogPage from "./pages/MyCatalogPage";
import AdminSubmissionsPage from "./pages/AdminSubmissionsPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import AdminHelpRequestsPage from "./pages/AdminHelpRequestsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CommunityPage from "./pages/CommunityPage";
import ImportItemPage from "./pages/ImportItemPage";
import NotFoundPage from "./pages/NotFoundPage";
import "./styles/live-pulse-ticker.css";
import LivePulseTicker from "./components/LivePulseTicker.jsx";
import { installAuthAxiosInterceptor, refreshApiSession, registerSessionRefreshHandler } from "./lib/authAxios";
import { clearGuestCartId, installGuestCartInterceptors } from "./lib/cartIdentity";
import { resolveApiBase } from "./lib/apiBase";
import { installChunkErrorRecovery } from "./lib/chunkRecovery";

const BACKEND_URL = resolveApiBase({
  hostname: typeof window !== "undefined" ? window.location.hostname : "",
  configured: process.env.REACT_APP_BACKEND_URL,
});
export const API = `${BACKEND_URL}/api`;

installAuthAxiosInterceptor({ apiBase: API });
installGuestCartInterceptors();
installChunkErrorRecovery();

// Context
const AuthContext = createContext(null);
const DestinationContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useDestination = () => useContext(DestinationContext);

// Mansion deploy stamp: 2026-06-20T02:18Z — catalog images + mobile products UI

const SITE_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://cheepsweepz.shop";
axios.defaults.headers.common["X-Site-Origin"] = SITE_ORIGIN;
axios.defaults.headers.common["X-DHC-Hub"] = "digitalhustlerscaribbean.com";
// Tenant marker for the shared DHC host. The CheepSweepz API in backend/ does
// not branch on it today; it is sent so requests are attributable at the hub.
axios.defaults.headers.common["X-Tenant-ID"] = "cheepsweepz";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-slate-500">
          {adminOnly ? "Loading admin panel..." : "Loading..."}
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * /checkout serves two flows: the normal cart checkout, and paying an order an
 * admin created by hand (?order_id=...), whose cart is empty by definition.
 */
function CheckoutRoute() {
  const [searchParams] = useSearchParams();
  const manualOrderId = searchParams.get("order_id");
  return manualOrderId ? (
    <ManualOrderPaymentPage orderId={manualOrderId} />
  ) : (
    <CheckoutPage />
  );
}

function searchParamsHasAuthCode() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("code");
}

function hashHasAccessToken() {
  if (typeof window === "undefined") return false;
  return window.location.hash?.includes("access_token=");
}

/**
 * Route-scoped crash guard. Keying on the pathname means a crash inside one
 * page shows the fallback instead of a white screen, and navigating elsewhere
 * clears it automatically.
 */
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

function AppRouter() {
  const location = useLocation();

  if (location.pathname !== "/auth/callback") {
    if (hashHasAccessToken()) {
      return <Navigate to={`/auth/callback${location.search}${location.hash}`} replace />;
    }
    if (searchParamsHasAuthCode()) {
      return <Navigate to={`/auth/callback${location.search}`} replace />;
    }
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/global-shipping" element={<GlobalShippingPage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:productId" element={<ProductDetailPage />} />
      <Route path="/imports/:category/:item" element={<ImportItemPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<SupabaseAuthCallback />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <CheckoutRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <OrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          <ProtectedRoute>
            <OrderDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/submit-link"
        element={
          <ProtectedRoute adminOnly>
            <AdminSubmitLinkPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute adminOnly>
            <AdminProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute adminOnly>
            <AdminOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute adminOnly>
            <AdminPendingPaymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/submissions"
        element={
          <ProtectedRoute adminOnly>
            <AdminSubmissionsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/submit" element={<SubmitProductPage />} />
      {/* Aliases for the URLs used in the nav label, docs and hand-off notes.
          Without these they fall through to the 404 page. */}
      <Route path="/submit-link" element={<Navigate to="/submit" replace />} />
      <Route path="/auth" element={<Navigate to="/login" replace />} />
      <Route path="/signin" element={<Navigate to="/login" replace />} />
      <Route path="/sign-up" element={<Navigate to="/register" replace />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route
        path="/my-catalog"
        element={
          <ProtectedRoute>
            <MyCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/order-confirmation"
        element={
          <ProtectedRoute>
            <OrderConfirmationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/help-requests"
        element={
          <ProtectedRoute adminOnly>
            <AdminHelpRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [destination, setDestination] = useState("BB");
  const [currency, setCurrency] = useState("USD");
  const [shippingSpeed, setShippingSpeed] = useState("standard");

  useEffect(() => {
    const pingBackend = async () => {
      try {
        await axios.get(`${API}/ping`, { timeout: 10000, withCredentials: false });
      } catch (error) {
        console.debug("Keep-alive ping failed (server may be sleeping)");
      }
    };

    pingBackend();
    const interval = setInterval(pingBackend, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    registerSessionRefreshHandler((userData) => {
      setUser(userData);
    });
    return () => registerSessionRefreshHandler(null);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          timeout: 15000,
        });
        setUser(response.data);
      } catch (error) {
        // Cross-site cookies are blocked on app.cheepsweepz.shop; if the JWT
        // expired, try exchanging a live Supabase session before clearing user.
        if (error.response?.status === 401) {
          try {
            const refreshed = await refreshApiSession(API);
            if (refreshed) {
              setUser(refreshed);
              return;
            }
          } catch (refreshError) {
            console.debug("Session refresh unavailable:", refreshError);
          }
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    if (token) {
      localStorage.setItem("token", token);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem("token");
    clearGuestCartId();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      <DestinationContext.Provider
        value={{
          destination,
          setDestination,
          currency,
          setCurrency,
          shippingSpeed,
          setShippingSpeed,
        }}
      >
        <div className="App-container min-h-screen w-full">
          <div
            className="fixed top-0 left-0 w-full h-10 overflow-hidden bg-[#0a0a0a] z-[99998]"
            style={{ height: "40px" }}
            data-live-pulse-ticker-wrapper
          >
            <LivePulseTicker siteRole="cheepsweepz" badgeLabel="CHEEPSWEEPZ" />
          </div>
          <BrowserRouter>
            <ScrollToTop />
            <main className="pt-[40px] min-h-screen">
              <Toaster position="top-right" richColors />
              <RouteErrorBoundary>
                <AppRouter />
              </RouteErrorBoundary>
            </main>
          </BrowserRouter>
        </div>
      </DestinationContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
